import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONFIG, SERVICES } from "@/lib/data";
import { submitEnquiry } from "@/lib/api";
import { Reveal, SectionLabel } from "@/components/Reveal";

const BUDGETS = ["< ₹5L", "₹5–15L", "₹15–40L", "₹40L+"];

export const Contact = ({ onBook }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", service: "", budget: "₹5–15L", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const whatsapp = `https://wa.me/${CONFIG.whatsapp}?text=${encodeURIComponent(CONFIG.whatsappMsg)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill name, email and a message.");
    setLoading(true);
    try {
      await submitEnquiry({ ...form, kind: "enquiry" });
      toast.success("Enquiry sent — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", service: "", budget: "₹5–15L", message: "" });
    } catch {
      toast.error("Couldn't send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative mx-auto max-w-[1400px] px-6 sm:px-10 py-24 lg:py-36">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16">
        <div>
          <Reveal>
            <SectionLabel>Start a project</SectionLabel>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
              Let's build something <span className="text-gradient">iconic.</span>
            </h2>
            <p className="mt-6 font-body text-[var(--text-secondary)] max-w-md leading-relaxed">
              Tell us about your brand and goals. Prefer to talk? Book a call or ping us on WhatsApp — whichever's faster for you.
            </p>
          </Reveal>

          <div className="mt-9 space-y-3">
            <a data-testid="whatsapp-chat-button" href={whatsapp} target="_blank" rel="noopener noreferrer"
               className="group flex items-center justify-between rounded-2xl glass p-5 hover-card-glow">
              <span className="flex items-center gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-xl btn-accent"><MessageCircle size={20} /></span>
                <span>
                  <span className="block font-heading font-semibold">Chat on WhatsApp</span>
                  <span className="block font-mono text-xs text-[var(--text-muted)]">+91 78509 11096</span>
                </span>
              </span>
              <ArrowRight className="text-[var(--text-muted)] group-hover:text-[var(--accent-primary)] group-hover:translate-x-1 transition-all" size={20} />
            </a>
            <button data-testid="calendly-modal-trigger" onClick={onBook}
                    className="group flex w-full items-center justify-between rounded-2xl glass p-5 hover-card-glow text-left">
              <span className="flex items-center gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-xl btn-accent"><Mail size={20} /></span>
                <span>
                  <span className="block font-heading font-semibold">Book a discovery call</span>
                  <span className="block font-mono text-xs text-[var(--text-muted)]">30 min · free · no pressure</span>
                </span>
              </span>
              <ArrowRight className="text-[var(--text-muted)] group-hover:text-[var(--accent-primary)] group-hover:translate-x-1 transition-all" size={20} />
            </button>
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="glass rounded-3xl p-7 sm:p-9 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="contact-form-name-input" value={form.name} onChange={set("name")} placeholder="Full name *" className="field" />
              <input data-testid="contact-form-email-input" value={form.email} onChange={set("email")} type="email" placeholder="Email *" className="field" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.company} onChange={set("company")} placeholder="Company" className="field" />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone" className="field" />
            </div>
            <select data-testid="contact-form-service" value={form.service} onChange={set("service")} className="field">
              <option value="">Service you need</option>
              {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
              <option value="Product Photography">Product Photography</option>
              <option value="Drone Shoots">Drone Shoots</option>
            </select>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] mb-2">Budget range</div>
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => (
                  <button type="button" key={b} onClick={() => setForm((f) => ({ ...f, budget: b }))}
                          className={`rounded-full px-4 py-2 text-sm transition-all ${form.budget === b ? "btn-accent" : "border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-hover)]"}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
            <textarea data-testid="contact-form-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project *" rows={4} className="field resize-none" />
            <button type="submit" disabled={loading} data-testid="contact-form-submit-btn"
                    className="btn-accent w-full rounded-full py-4 font-heading font-semibold disabled:opacity-60">
              {loading ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
